import { useEffect, useState, type CSSProperties } from "react";
import type { ReaderWorkspace } from "../../../reader/reader-workspace";
import { getReaderTextConverter } from "../../../reader/reader-text-converter";

export function ReaderContent({
  workspace,
  activeChapterId,
}: {
  workspace: ReaderWorkspace;
  activeChapterId?: string;
}) {
  const preference = workspace.preference;
  const [textConverter, setTextConverter] = useState<((text: string) => string) | null>(null);
  const chapters = activeChapterId
    ? [workspace.openedChapters.find(chapter => chapter.chapterId === activeChapterId) ?? workspace.opened?.chapter].filter((chapter): chapter is NonNullable<typeof chapter> => Boolean(chapter))
    : workspace.opened
      ? [workspace.opened.chapter]
      : [];

  useEffect(() => {
    let cancelled = false;

    setTextConverter(null);
    void getReaderTextConverter(preference.script)
      .then(converter => {
        if (!cancelled) setTextConverter(() => converter);
      })
      .catch(() => {
        if (!cancelled) setTextConverter(null);
      });

    return () => {
      cancelled = true;
    };
  }, [preference.script]);

  return (
    <div
      className="space-y-10 text-pretty"
      style={
        {
          fontFamily: preference.fontFamily,
          fontWeight: preference.fontWeight,
          fontSize: `${preference.fontSize}px`,
          lineHeight: preference.lineHeight,
          letterSpacing: `${preference.letterSpacing}px`,
          textAlign: preference.textAlign,
          textAlignLast: preference.textAlignLast,
          textJustify: preference.textAlign === "justify" ? "inter-character" : undefined,
          padding: `${preference.paddingTop}px ${preference.paddingRight}px ${preference.paddingBottom}px ${preference.paddingLeft}px`,
        } as CSSProperties
      }
    >
      {chapters.map(chapter => {
        return (
          <section key={chapter.chapterId} data-reader-chapter-id={chapter.chapterId}>
            {chapter.blocks.map((block, index) => {
              if (block.type === "heading") return null;

              if (block.type === "paragraph") {
                return (
                  <p
                    key={index}
                    data-reader-paragraph-index={index + 1}
                    style={{
                      marginBottom: `${preference.paragraphSpacing}px`,
                      textIndent: `${preference.textIndent}em`,
                    }}
                  >
                    {textConverter ? textConverter(block.text ?? "") : block.text}
                  </p>
                );
              }

              if (block.type === "image" && block.src) {
                return (
                  <figure
                    key={index}
                    data-reader-paragraph-index={index + 1}
                    className="overflow-hidden border bg-muted/30"
                    style={{ marginBottom: `${preference.paragraphSpacing}px` }}
                  >
                    <img src={block.src} alt={block.alt ?? ""} width={block.width} height={block.height} className="mx-auto max-h-[80vh] w-full object-contain" loading="lazy" />
                    {block.alt && <figcaption className="px-3 py-2 text-center text-xs text-muted-foreground">{block.alt}</figcaption>}
                  </figure>
                );
              }

              if (block.type === "divider") {
                return <hr key={index} data-reader-paragraph-index={index + 1} style={{ marginBottom: `${preference.paragraphSpacing}px` }} />;
              }
              return null;
            })}
          </section>
        );
      })}
    </div>
  );
}
